import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/authContext";
import FloatingNavigation from "../components/FloatingNavigation";
import {
  FaSearch,
  FaFilter,
  FaDownload,
  FaArchive,
  FaUndo,
  FaTrash,
  FaEye,
  FaEyeSlash,
  FaExclamationTriangle,
  FaUser,
  FaMapMarkerAlt,
  FaClock,
  FaStickyNote,
  FaBell,
  FaCheckCircle
} from "react-icons/fa";

export default function ManageNotes() {
  const { token } = useAuth();
  const [notes, setNotes] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [agentFilter, setAgentFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all, unread, read, archived
  const [searchQuery, setSearchQuery] = useState("");
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNotes, setSelectedNotes] = useState([]);

  useEffect(() => {
    if (token) {
      fetchNotes();
      fetchAgents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/get-notes`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const sorted = res.data.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setNotes(sorted);
      setFiltered(sorted);
    } catch (err) {
      console.error("Failed to fetch notes:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/get-users?role=Agent`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setAgents(res.data);
    } catch (err) {
      console.error("Failed to fetch agents:", err);
    }
  };

  const filterNotes = () => {
    const filtered = notes.filter((note) => {
      const matchesAgent = !agentFilter || note.agentName === agentFilter;
      const matchesPriority = !priorityFilter || note.priority === priorityFilter;
      const matchesSearch = !searchQuery || 
        note.note.toLowerCase().includes(searchQuery.toLowerCase()) ||
        note.agentName.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Status filtering
      let matchesStatus = true;
      if (statusFilter === "read") {
        matchesStatus = note.isRead === true;
      } else if (statusFilter === "unread") {
        matchesStatus = note.isRead !== true;
      } else if (statusFilter === "archived") {
        matchesStatus = note.isArchived === true;
      } else if (statusFilter === "active") {
        matchesStatus = note.isArchived !== true;
      }
      
      return matchesAgent && matchesPriority && matchesSearch && matchesStatus;
    });
    setFiltered(filtered);
  };

  useEffect(() => {
    filterNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentFilter, priorityFilter, statusFilter, searchQuery, notes]);

  const handleDelete = async (id) => {
    const note = notes.find(n => n._id === id);
    const confirmDelete = window.confirm(
      `Are you sure you want to delete this note?\n\nNote: "${note?.note?.substring(0, 50)}..."\nAgent: ${note?.agentName}\n\nThis action cannot be undone.`
    );
    if (!confirmDelete) return;
    
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/add-notes/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      alert("✅ Note deleted successfully.");
      fetchNotes();
    } catch (err) {
      console.error("Delete failed:", err);
      alert("❌ Failed to delete note. Please try again.");
    }
  };

  // New functions for enhanced note management
  const handleMarkAsRead = async (id, isRead = true) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/notes/${id}/read`,
        { isRead },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchNotes();
    } catch (err) {
      console.error("Failed to update read status:", err);
    }
  };

  const handleArchive = async (id, isArchived = true) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/notes/${id}/archive`,
        { isArchived },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchNotes();
    } catch (err) {
      console.error("Failed to update archive status:", err);
    }
  };

  const handlePriorityChange = async (id, priority) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/notes/${id}/priority`,
        { priority },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchNotes();
    } catch (err) {
      console.error("Failed to update priority:", err);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedNotes.length === 0) {
      alert("Please select notes first.");
      return;
    }

    const confirmAction = window.confirm(
      `Are you sure you want to ${action} ${selectedNotes.length} selected note(s)?`
    );
    if (!confirmAction) return;

    try {
      const promises = selectedNotes.map(id => {
        switch (action) {
          case "delete":
            return axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/add-notes/${id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
          case "archive":
            return axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/notes/${id}/archive`, 
              { isArchived: true }, { headers: { Authorization: `Bearer ${token}` } });
          case "unarchive":
            return axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/notes/${id}/archive`, 
              { isArchived: false }, { headers: { Authorization: `Bearer ${token}` } });
          case "mark-read":
            return axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/notes/${id}/read`, 
              { isRead: true }, { headers: { Authorization: `Bearer ${token}` } });
          case "mark-unread":
            return axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/notes/${id}/read`, 
              { isRead: false }, { headers: { Authorization: `Bearer ${token}` } });
          default:
            return Promise.resolve();
        }
      });

      await Promise.all(promises);
      setSelectedNotes([]);
      fetchNotes();
      alert(`✅ Successfully ${action}d ${selectedNotes.length} note(s).`);
    } catch (err) {
      console.error(`Bulk ${action} failed:`, err);
      alert(`❌ Failed to ${action} some notes. Please try again.`);
    }
  };

  const toggleNoteSelection = (id) => {
    setSelectedNotes(prev => 
      prev.includes(id) ? prev.filter(noteId => noteId !== id) : [...prev, id]
    );
  };

  const selectAllNotes = () => {
    setSelectedNotes(filtered.map(note => note._id));
  };

  const clearSelection = () => {
    setSelectedNotes([]);
  };

  const exportToCSV = () => {
    const csvData = filtered.map(note => ({
      "Agent": note.agentName,
      "Site": note.siteName || "N/A",
      "Priority": note.priority,
      "Status": note.isArchived ? "Archived" : (note.isRead ? "Read" : "Unread"),
      "Note": note.note.replace(/,/g, ";"), // Replace commas to avoid CSV issues
      "Created": formatDateTime(note.createdAt)
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(","),
      ...csvData.map(row => Object.values(row).map(val => `"${val}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `notes-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const getStats = () => {
    return {
      total: notes.length,
      unread: notes.filter(n => !n.isRead && !n.isArchived).length,
      urgent: notes.filter(n => n.priority === "Urgent" && !n.isArchived).length,
      archived: notes.filter(n => n.isArchived).length,
    };
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString("en-LK", { timeZone: "Asia/Colombo" });
  };

  const highlightText = (text) => {
    if (!searchQuery) return text;
    const regex = new RegExp(`(${searchQuery})`, "gi");
    return text
      .split(regex)
      .map((part, i) =>
        regex.test(part) ? <mark key={i}>{part}</mark> : part
      );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Notes Management</h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage agent notes, priorities, and track important communications
              </p>
            </div>
            <div className="mt-4 sm:mt-0 flex space-x-3">
              <button
                onClick={exportToCSV}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FaDownload className="mr-2" />
                Export
              </button>
              {selectedNotes.length > 0 && (
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleBulkAction("mark-read")}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <FaCheckCircle className="mr-1" />
                    Mark Read
                  </button>
                  <button
                    onClick={() => handleBulkAction("archive")}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <FaArchive className="mr-1" />
                    Archive
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-blue-100">
                <FaStickyNote className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Notes</p>
                <p className="text-2xl font-semibold text-gray-900">{getStats().total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-yellow-100">
                <FaBell className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Unread</p>
                <p className="text-2xl font-semibold text-gray-900">{getStats().unread}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-red-100">
                <FaExclamationTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Urgent</p>
                <p className="text-2xl font-semibold text-gray-900">{getStats().urgent}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-gray-100">
                <FaArchive className="h-6 w-6 text-gray-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Archived</p>
                <p className="text-2xl font-semibold text-gray-900">{getStats().archived}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Notes
              </label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by content or agent..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Agent
              </label>
              <select
                value={agentFilter}
                onChange={(e) => setAgentFilter(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">All Agents</option>
                {agents.map((agent) => (
                  <option key={agent._id} value={agent.name}>
                    {agent.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">All Priorities</option>
                <option value="Normal">Normal</option>
                <option value="Urgent">Urgent</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="all">All Notes</option>
                <option value="active">Active Only</option>
                <option value="unread">Unread Only</option>
                <option value="read">Read Only</option>
                <option value="archived">Archived Only</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearchQuery("");
                  setAgentFilter("");
                  setPriorityFilter("");
                  setStatusFilter("all");
                }}
                className="w-full px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FaFilter className="inline mr-2" />
                Clear Filters
              </button>
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedNotes.length > 0 && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-blue-900">
                  {selectedNotes.length} note(s) selected
                </span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleBulkAction("mark-read")}
                    className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded hover:bg-green-200"
                  >
                    Mark Read
                  </button>
                  <button
                    onClick={() => handleBulkAction("mark-unread")}
                    className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded hover:bg-yellow-200"
                  >
                    Mark Unread
                  </button>
                  <button
                    onClick={() => handleBulkAction("archive")}
                    className="text-xs px-2 py-1 bg-gray-100 text-gray-800 rounded hover:bg-gray-200"
                  >
                    Archive
                  </button>
                  <button
                    onClick={() => handleBulkAction("delete")}
                    className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded hover:bg-red-200"
                  >
                    Delete
                  </button>
                  <button
                    onClick={clearSelection}
                    className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Notes List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Notes ({filtered.length})
              </h3>
              <div className="flex items-center space-x-3">
                <button
                  onClick={selectedNotes.length === filtered.length ? clearSelection : selectAllNotes}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {selectedNotes.length === filtered.length ? "Deselect All" : "Select All"}
                </button>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading notes...</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12">
                <FaStickyNote className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No notes found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchQuery || agentFilter || priorityFilter || statusFilter !== "all"
                    ? "Try adjusting your search or filters"
                    : "No notes have been created yet"}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filtered.map((note) => (
                  <div
                    key={note._id}
                    className={`border rounded-lg p-4 transition-all duration-200 hover:shadow-md ${
                      selectedNotes.includes(note._id)
                        ? "border-blue-500 bg-blue-50"
                        : note.isArchived
                        ? "border-gray-300 bg-gray-50 opacity-75"
                        : note.isRead
                        ? "border-gray-200"
                        : "border-l-4 border-l-blue-500 border-t-gray-200 border-r-gray-200 border-b-gray-200 bg-blue-50"
                    }`}
                  >
                    <div className="flex items-start space-x-4">
                      {/* Selection Checkbox */}
                      <div className="flex-shrink-0 pt-1">
                        <input
                          type="checkbox"
                          checked={selectedNotes.includes(note._id)}
                          onChange={() => toggleNoteSelection(note._id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>

                      {/* Note Content */}
                      <div className="flex-1 min-w-0">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            <div className="flex items-center text-sm text-gray-600">
                              <FaUser className="mr-1" />
                              <span className="font-medium">{note.agentName}</span>
                            </div>
                            {note.siteName && (
                              <div className="flex items-center text-sm text-gray-500">
                                <FaMapMarkerAlt className="mr-1" />
                                <span>{note.siteName}</span>
                              </div>
                            )}
                            <div className="flex items-center text-sm text-gray-500">
                              <FaClock className="mr-1" />
                              <span>{formatDateTime(note.createdAt)}</span>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            {/* Priority Badge */}
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                note.priority === "Urgent"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {note.priority === "Urgent" && <FaExclamationTriangle className="mr-1" />}
                              {note.priority}
                            </span>

                            {/* Status Badges */}
                            {note.isArchived ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                <FaArchive className="mr-1" />
                                Archived
                              </span>
                            ) : (
                              <span
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  note.isRead
                                    ? "bg-green-100 text-green-800"
                                    : "bg-yellow-100 text-yellow-800"
                                }`}
                              >
                                {note.isRead ? <FaCheckCircle className="mr-1" /> : <FaBell className="mr-1" />}
                                {note.isRead ? "Read" : "Unread"}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Note Content */}
                        <div className="text-gray-900 mb-3">
                          {highlightText(note.note)}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            {!note.isArchived && (
                              <>
                                <button
                                  onClick={() => handleMarkAsRead(note._id, !note.isRead)}
                                  className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded ${
                                    note.isRead
                                      ? "text-yellow-700 bg-yellow-100 hover:bg-yellow-200"
                                      : "text-green-700 bg-green-100 hover:bg-green-200"
                                  }`}
                                  title={note.isRead ? "Mark as Unread" : "Mark as Read"}
                                >
                                  {note.isRead ? <FaEyeSlash className="mr-1" /> : <FaEye className="mr-1" />}
                                  {note.isRead ? "Unread" : "Read"}
                                </button>
                                
                                <select
                                  value={note.priority}
                                  onChange={(e) => handlePriorityChange(note._id, e.target.value)}
                                  className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                  <option value="Normal">Normal</option>
                                  <option value="Urgent">Urgent</option>
                                </select>
                              </>
                            )}
                          </div>

                          <div className="flex items-center space-x-1">
                            {note.isArchived ? (
                              <button
                                onClick={() => handleArchive(note._id, false)}
                                className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                                title="Unarchive"
                              >
                                <FaUndo />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleArchive(note._id, true)}
                                className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                title="Archive"
                              >
                                <FaArchive />
                              </button>
                            )}
                            
                            <button
                              onClick={() => handleDelete(note._id)}
                              className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
                              title="Delete"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <FloatingNavigation />
    </div>
  );
}
